import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import Api from '@/modules/core/api/api.ts';
import GatewayApi from '@/modules/core/api/gateway.api.ts';
import { GatewayFilter } from '@/modules/core/utils/gateway-filter.ts';
import { notification } from '@/modules/common/composables/use-notification.ts';
import type { ItemsDto } from '@/modules/common/dto/items.dto.ts';
import type { ViewerQuery } from '@/modules/viewer/types/query.type.ts';
import { CounterpartyAdapter } from '@/modules/counterparty/adapters/counterparty.adapter.ts';
import { CounterpartyEndPoint } from '@/modules/counterparty/enums/counterparty-end-point.enum.ts';
import { COUNTERPARTY_DISTINCT_FIELDS } from '@/modules/counterparty/enums/counterparty-distinct-field.enum.ts';
import type { CounterpartyDto } from '@/modules/counterparty/dto/counterparty.dto.ts';
import type { CounterpartyTypeDto } from '@/modules/counterparty/dto/counterparty-type.dto.ts';
import { CounterpartyTypeAdapter } from '@/modules/counterparty/adapters/counterparty-type.adapter.ts';
import { CounterpartyInnAdapter } from '@/modules/counterparty/adapters/counterparty-inn.adapter.ts';
import { CounterpartyQueryOptions } from '@/modules/counterparty/query-options/counterparty.query-options.ts';
import { LegalFormQueryOptions } from '@/modules/counterparty/query-options/legal-form.query-options.ts';
import ClientCard from '@/modules/counterparty/models/card/client-card.ts';
import ClientOption from '@/modules/common/models/option/client-option.ts';
import { OptionsUtil } from '@/modules/common/utils/options.util.ts';
import type { FnsLegalEntityDto } from '@/modules/counterparty/dto/fns-legal-entity.dto.ts';
import type { CounterpartyInnResult } from '@/modules/counterparty/types/counterparty-inn-result.type.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import { queryClient } from '@/tanstack-query.ts';
import { requirePositiveId } from '@/modules/common/utils/number.util.ts';
import { IgnorableException } from '@/modules/common/models/exception/ignorable-exception.ts';
import { CounterpartyInternalAdapter } from '@/modules/counterparty/adapters/counterparty-internal.adapter.ts';
import type {
  CounterpartyInternalDto,
  CounterpartyManagerIdDto,
} from '@/modules/counterparty/dto/counterparty-internal.dto.ts';
import type { CounterpartyEditParams } from '@/modules/counterparty/types/counterparty-internal.type.ts';

type MyClientsCountResponse = {
  items?: {
    my_clients_count?: string | number | null;
  };
};

type CounterpartyKindEditPatch = {
  typeId?: number;
  category?: string;
};

function textOrEmpty(value: unknown) {
  return value == null ? '' : String(value).trim();
}

function positiveId(value: unknown) {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function failKindEdit(): never {
  notification.error('counterparty.notification.error.kind.incomplete');
  throw new IgnorableException(null);
}

function buildCounterpartyEditBody(
  clientId: number,
  row: Record<string, unknown>,
  patch: CounterpartyKindEditPatch = {}
) {
  const name = textOrEmpty(row['Название']);
  const category = textOrEmpty(patch.category ?? row['Приоритет']).toUpperCase();
  const typeId = patch.typeId ?? positiveId(row.TYPE_ID);
  const managerId = positiveId(row.MANAGER_ID);
  const regionId = positiveId(row.REGION_ID);
  const segmentId = positiveId(row.SEGMENT_ID);
  const discount = Number(row['Скидка']);
  const lfId = positiveId(row.LF_ID);

  if (
    !name ||
    !['A', 'B', 'C'].includes(category) ||
    typeId == null ||
    managerId == null ||
    regionId == null ||
    segmentId == null
  ) {
    return null;
  }

  return {
    client_id: clientId,
    type_id: typeId,
    name,
    manager_id: managerId,
    region_id: regionId,
    segment_id: segmentId,
    category,
    parents: textOrEmpty(row.PARENTS),
    discount: Number.isFinite(discount) ? discount : 0,
    comment: textOrEmpty(row['Примечание']),
    inn: textOrEmpty(row['ИНН']),
    kpp: textOrEmpty(row['КПП']),
    okato: textOrEmpty(row['ОКАТО']),
    okpo: textOrEmpty(row['ОКПО']),
    position: textOrEmpty(row['Должность']),
    document: textOrEmpty(row['ДУЛ']),
    ogrn: textOrEmpty(row['ОГРН']),
    ...(lfId != null ? { lf_id: lfId } : {}),
    city: textOrEmpty(row['Город']),
    code: textOrEmpty(row['Код']) || textOrEmpty(row['Штрих']),
  };
}

type ClientOptionItem = {
  CLIENT_ID: number;
  Название: string;
  Доступен: number;
};

export type CounterpartyNameMatch = {
  id: number;
  name: string;
};

export type CounterpartyAddParams = {
  typeId: number;
  name: string;
  managerId: number;
  regionId: number;
  segmentId: number;
  category: string;
  parents?: string;
  discount?: number;
  comment?: string;
  inn?: string;
  kpp?: string;
  okato?: string;
  okpo?: string;
  position?: string;
  document?: string;
  ogrn?: string;
  legalFormId?: number;
  city?: string;
  code?: string;
};

export class CounterpartyApi {
  static async getPage(request: ViewerQuery, signal: AbortSignal) {
    try {
      const response = await GatewayApi.getList<CounterpartyDto>(CounterpartyEndPoint.GetList, request, signal);
      return CounterpartyAdapter.mapPage(response, request.page);
    } catch (error) {
      if (ErrorUtil.isUnexpected(error)) {
        notification.error('counterparty.error.getList', error);
      }

      throw error;
    }
  }

  static async getCount(signal: AbortSignal) {
    const response: MyClientsCountResponse = await Api.post(CounterpartyEndPoint.GetCount, {}, signal);
    const count = Number(response.items?.my_clients_count);

    return Number.isFinite(count) ? count : 0;
  }

  static getDistinct(signal: AbortSignal) {
    return GatewayApi.getDistinct(CounterpartyEndPoint.GetList, COUNTERPARTY_DISTINCT_FIELDS, signal);
  }

  static async getTypes(signal?: AbortSignal) {
    const response: ItemsDto<CounterpartyTypeDto> = await Api.post(CounterpartyEndPoint.GetTypes, {}, signal);
    return CounterpartyTypeAdapter.mapList(response);
  }

  static async getTypeOptions(signal: AbortSignal): Promise<Option<number>[]> {
    const types = await queryClient.ensureQueryData(CounterpartyQueryOptions.getTypes(signal));
    return OptionsUtil.sortByLabel(types.map((type) => ({ code: type.id, label: type.name })));
  }

  static async getClient(id: number, signal?: AbortSignal) {
    const response = await GatewayApi.get<Record<string, unknown>>(
      CounterpartyEndPoint.GetList,
      'CLIENT_ID',
      id,
      signal
    );
    return new ClientCard(response);
  }

  static async getClients(ids: number[], signal?: AbortSignal) {
    const uniqueIds = [...new Set(ids.filter((id) => Number.isFinite(id)))];

    if (!uniqueIds.length) {
      return [];
    }

    const response = await GatewayApi.execute<ItemsDto<Record<string, unknown>>>(CounterpartyEndPoint.GetList, {
      filter: GatewayFilter.and(GatewayFilter.in('CLIENT_ID', uniqueIds)),
      limit: uniqueIds.length,
      signal,
    });

    const byId = new Map(
      (response.items ?? []).map((item) => [Number(item['CLIENT_ID']), new ClientCard(item)] as const)
    );

    return uniqueIds.map((id) => byId.get(id)).filter((card): card is ClientCard => !!card);
  }

  static async getOptions(query: string, signal?: AbortSignal): Promise<ClientOption[]> {
    const response: ItemsDto<ClientOptionItem> = await Api.post(CounterpartyEndPoint.GetOptions, { query }, signal);

    return OptionsUtil.sortByLabel(response.items.map((item) => new ClientOption(item)));
  }

  static async checkInnExists(inn: string, signal?: AbortSignal): Promise<boolean> {
    const safeInn = inn.replace(/\D/gu, '');
    if (!safeInn) {
      return false;
    }

    const response = await GatewayApi.execute<ItemsDto<CounterpartyDto>>(CounterpartyEndPoint.GetList, {
      filter: GatewayFilter.and(GatewayFilter.eq('ИНН', safeInn)),
      limit: 1,
      signal,
    });
    return Boolean(response.items?.length);
  }

  static async checkNameExists(name: string, signal?: AbortSignal): Promise<boolean> {
    const trimmed = name.trim();
    if (!trimmed) {
      return false;
    }

    const variants = [...new Set([trimmed, trimmed.toLocaleLowerCase(), trimmed.toLocaleUpperCase()])];

    const response = await GatewayApi.execute<ItemsDto<CounterpartyDto>>(CounterpartyEndPoint.GetList, {
      filter: GatewayFilter.or(...variants.map((value) => GatewayFilter.eq('Название', value))),
      limit: 1,
      signal,
    });
    return Boolean(response.items?.length);
  }

  static async searchByName(name: string, signal?: AbortSignal): Promise<CounterpartyNameMatch[]> {
    const trimmed = name.trim();
    if (!trimmed) {
      return [];
    }

    const response: ItemsDto<ClientOptionItem> = await Api.post(
      CounterpartyEndPoint.GetOptions,
      { query: trimmed },
      signal
    );
    const matches = new Map<number, CounterpartyNameMatch>();

    for (const item of response.items ?? []) {
      const label = item.Название?.trim();

      if (!label || matches.has(item.CLIENT_ID)) {
        continue;
      }

      matches.set(item.CLIENT_ID, { id: item.CLIENT_ID, name: label });
    }

    return [...matches.values()];
  }

  static async searchByInn(inn: string, signal?: AbortSignal): Promise<CounterpartyInnResult | null> {
    const response: FnsLegalEntityDto | null = await Api.post(
      CounterpartyEndPoint.FnsLegalEntityGet,
      { inn: Number(inn) },
      signal
    );

    const name = response?.short_name || response?.full_name || '';
    if (!response || (!response.inn && !name)) {
      return null;
    }

    const legalForms = await queryClient.ensureQueryData(LegalFormQueryOptions.getList(signal));

    return CounterpartyInnAdapter.map(response, CounterpartyInnAdapter.matchLegalForm(name, legalForms));
  }

  static async add(parameters: CounterpartyAddParams, signal?: AbortSignal) {
    const response = await Api.post(
      CounterpartyEndPoint.Add,
      {
        type_id: parameters.typeId,
        name: parameters.name,
        manager_id: parameters.managerId,
        region_id: parameters.regionId,
        segment_id: parameters.segmentId,
        category: parameters.category,
        parents: parameters.parents,
        discount: parameters.discount,
        comment: parameters.comment,
        inn: parameters.inn,
        kpp: parameters.kpp,
        okato: parameters.okato,
        okpo: parameters.okpo,
        position: parameters.position,
        document: parameters.document,
        ogrn: parameters.ogrn,
        lf_id: parameters.legalFormId,
        city: parameters.city,
        code: parameters.code,
      },
      signal
    );

    return requirePositiveId(response.client_id, 'The counterparty id is not defined.');
  }

  static async getInternal(id: number, signal?: AbortSignal) {
    const response = await GatewayApi.get<CounterpartyInternalDto>(
      CounterpartyEndPoint.GetInternal,
      'CLIENT_ID',
      id,
      signal
    );
    const counterparty = CounterpartyInternalAdapter.map(response);

    if (counterparty.managerId === null) {
      counterparty.managerId = await CounterpartyApi.getManagerId(id, signal);
    }

    return counterparty;
  }

  static async getManagerId(id: number, signal?: AbortSignal) {
    const response: CounterpartyManagerIdDto | null = await Api.post(
      CounterpartyEndPoint.GetManagerId,
      { client_id: id },
      signal
    );

    return CounterpartyInternalAdapter.mapManagerId(response);
  }

  static async editKind(clientId: number, patch: CounterpartyKindEditPatch = {}, signal?: AbortSignal) {
    const row = await GatewayApi.get<Record<string, unknown>>(
      CounterpartyEndPoint.GetInternal,
      'CLIENT_ID',
      clientId,
      signal
    );

    if (positiveId(row.MANAGER_ID) == null) {
      const managerId = await CounterpartyApi.getManagerId(clientId, signal);

      if (managerId == null) {
        failKindEdit();
      }

      row.MANAGER_ID = managerId;
    }

    const body = buildCounterpartyEditBody(clientId, row, patch);

    if (!body) {
      failKindEdit();
    }

    await Api.post(CounterpartyEndPoint.Edit, body, signal);
  }

  static async edit(parameters: CounterpartyEditParams, signal?: AbortSignal) {
    await Api.post(CounterpartyEndPoint.Edit, { ...CounterpartyInternalAdapter.toEditRequest(parameters) }, signal);
  }
}
